import VirtualGallery from '../VirtualGallery';
import useLatestRead from '../../hooks/useLatestRead';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generationsAPI } from '../../services/api';
import { getImageModelLabel } from '../../services/imageModels';
import ClipboardIcon from '../ClipboardIcon';
import OptimizedImage, { getPreviewImagePath } from '../OptimizedImage';
import Timer from '../Timer/Timer';
import './PromptLibrary.css';

const PromptLibrary = () => {
  const startRead = useLatestRead();
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMoreGenerations, setHasMoreGenerations] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const navigate = useNavigate();
  const INITIAL_LIMIT = 7;
  const BATCH_SIZE = 12;

  useEffect(() => {
    loadGenerations();
  }, []);

  const mergeGenerations = (current, incoming) => {
    const merged = new Map();
    [...current, ...incoming].forEach((generation) => {
      if (generation?.id) {
        merged.set(generation.id, generation);
      }
    });
    return Array.from(merged.values()).sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  };

  const loadGenerations = async ({ append = false, limit = INITIAL_LIMIT, offset = 0 } = {}) => {
    const read = startRead();
    if (!read.isCurrent()) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await generationsAPI.getLibrary({ limit, offset, signal: read.signal });
      if (!read.isCurrent()) return;
      // Already sorted by created_at DESC from backend
      const loaded = response.data || [];
      setGenerations((prev) => (append ? mergeGenerations(prev, loaded) : loaded));
      setHasMoreGenerations(loaded.length === limit);
    } catch (error) {
      if (!read.isCurrent()) return;
      console.error('Failed to load generations:', error);
    } finally {
      if (read.isCurrent()) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const handleShowMore = () => {
    if (!loadingMore && hasMoreGenerations) {
      loadGenerations({ append: true, limit: BATCH_SIZE, offset: generations.length });
    }
  };

  const handleCopyPrompt = async (prompt) => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
    } catch (error) {
      console.error('Failed to copy prompt:', error);
    }
  };

  const handleReplicate = (generation) => {
    if (!generation) return;
    navigate('/', {
      state: {
        prefillData: {
          prompt: generation.prompt,
          model: generation.model,
          aspect_ratio: generation.aspect_ratio,
          resolution: generation.resolution,
          output_format: generation.output_format,
          reference_images: generation.reference_images || [],
        },
      },
    });
  };

  const handleToggleLibrary = async (generationId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await generationsAPI.toggleLibrary(generationId, newStatus);

      // Update local state optimistically
      setGenerations(prevGens =>
        prevGens.map(gen =>
          gen.id === generationId ? { ...gen, in_library: newStatus } : gen
        )
      );

      // Also update selectedGeneration if it's the one being toggled
      if (selectedGeneration && selectedGeneration.id === generationId) {
        setSelectedGeneration(prev => ({ ...prev, in_library: newStatus }));
      }

      // If removing from library, also remove from list immediately
      if (!newStatus) {
        setGenerations(prevGens => prevGens.filter(gen => gen.id !== generationId));
        setSelectedGeneration(null);
      }
    } catch (error) {
      console.error('Failed to toggle library status:', error);
    }
  };

  const openModal = (generation) => {
    setSelectedGeneration(generation);
    setIsZoomed(false);
    setZoomLevel(1);
  };

  const closeModal = () => {
    setSelectedGeneration(null);
    setIsZoomed(false);
    setZoomLevel(1);
  };

  const toggleZoom = () => {
    setIsZoomed((prev) => {
      const next = !prev;
      setZoomLevel(next ? 1.5 : 1);
      return next;
    });
  };

  const handleWheelZoom = (event) => {
    if (!isZoomed) return;
    event.preventDefault();
    const direction = event.deltaY > 0 ? -0.15 : 0.15;

    setZoomLevel((prev) => {
      const next = Math.min(4, Math.max(1, prev + direction));

      if (next <= 1.01) {
        setIsZoomed(false);
        return 1;
      }

      return next;
    });
  };

  return (
    <div className="prompt-library-container">
      <div className="prompt-library-header">
        <h1>Prompt Library</h1>
        <p className="prompt-library-subtitle">Browse recent prompts and reuse them quickly</p>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : generations.length === 0 ? (
        <p className="empty-message">No prompts saved yet</p>
      ) : (
        <VirtualGallery className="prompt-library-list" singleColumn>
          {generations.map((gen, index) => (
            <div
              key={`prompt-${gen.id}`}
              className="prompt-library-row"
              onClick={() => openModal(gen)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openModal(gen);
                }
              }}
            >
              <div className="prompt-col prompt-text">{gen.prompt}</div>
              <div className="prompt-col prompt-thumb">
                <OptimizedImage src={getPreviewImagePath(gen)} alt={gen.prompt} eager={index < INITIAL_LIMIT} />
              </div>
              <div className="prompt-col prompt-model" title={gen.model || ''}>
                {gen.model ? `Modèle: ${getImageModelLabel(gen.model)}` : 'Modèle non renseigné'}
              </div>
              <div className="prompt-col prompt-copy">
                <button
                  type="button"
                  className="btn-copy-row"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt(gen.prompt);
                  }}
                >
                  Copy
                </button>
              </div>
              <div className="prompt-col prompt-references">
                <div className="prompt-ref-scroll">
                  {gen.reference_images && gen.reference_images.length > 0 ? (
                    gen.reference_images.map((ref, idx) => (
                      <div key={`${gen.id}-ref-${idx}`} className="prompt-ref-thumb">
                        <OptimizedImage src={getPreviewImagePath(ref)} alt={`Ref ${idx + 1}`} />
                      </div>
                    ))
                  ) : (
                    <span className="no-refs">No refs</span>
                  )}
                </div>
              </div>
              <div className="prompt-col prompt-replicate">
                <button
                  type="button"
                  className="btn-replicate"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReplicate(gen);
                  }}
                >
                  Replicate
                </button>
              </div>
            </div>
          ))}
        </VirtualGallery>
      )}

      {!loading && hasMoreGenerations && (
        <button
          type="button"
          className="btn-show-more-prompts"
          onClick={handleShowMore}
          disabled={loadingMore}
        >
          {loadingMore ? 'Loading...' : 'Show More'}
        </button>
      )}

      {selectedGeneration && (
        <div className="modal-overlay" onClick={closeModal}>
          <button className="modal-close" onClick={closeModal}>
            ×
          </button>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-image-section">
              <OptimizedImage
                src={selectedGeneration.image_path}
                alt={selectedGeneration.prompt}
                className={`modal-image ${isZoomed ? 'zoomed' : ''}`}
                onClick={toggleZoom}
                onWheel={handleWheelZoom}
                style={{ transform: isZoomed ? `scale(${zoomLevel})` : 'none' }}
                eager
              />
            </div>
            <div className="modal-details-section">
              <div className="modal-detail-group">
                <div className="modal-detail-label">Prompt</div>
                <div className="modal-detail-value modal-prompt-row">
                  <span>{selectedGeneration.prompt}</span>
                  <button
                    type="button"
                    className="btn-copy-prompt"
                    onClick={() => handleCopyPrompt(selectedGeneration.prompt)}
                    title="Copy prompt"
                    aria-label="Copy prompt"
                  >
                    <ClipboardIcon />
                  </button>
                </div>
              </div>

              <div className="modal-detail-group">
                <div className="modal-detail-label">Settings</div>
                <div className="modal-meta-tags">
                  {selectedGeneration.model && (
                    <span>Modèle: {getImageModelLabel(selectedGeneration.model)}</span>
                  )}
                  <span>{selectedGeneration.aspect_ratio}</span>
                  <span>{selectedGeneration.resolution}</span>
                  <span>{selectedGeneration.output_format.toUpperCase()}</span>
                </div>
              </div>

              {selectedGeneration.reference_images && selectedGeneration.reference_images.length > 0 && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Reference Images</div>
                  <div className="modal-reference-images">
                    {selectedGeneration.reference_images.map((ref, idx) => (
                      <div
                        key={idx}
                        className="modal-reference-thumbnail"
                        title={ref.reference_type === 'project' ? 'Project Reference' : 'Upload Reference'}
                      >
                        <OptimizedImage
                          src={getPreviewImagePath(ref)}
                          alt={`Reference ${idx + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-detail-group">
                <div className="modal-detail-label">Cost</div>
                <div className="modal-detail-value">€{parseFloat(selectedGeneration.cost || 0).toFixed(2)}</div>
              </div>

              {selectedGeneration.generation_time_ms && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Generation Time</div>
                  <div className="modal-detail-value">
                    <Timer startTime={0} endTime={selectedGeneration.generation_time_ms} className="completed" />
                  </div>
                </div>
              )}

              {selectedGeneration.username && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Created By</div>
                  <div className="modal-detail-value">
                    <span className="modal-username">@{selectedGeneration.username}</span>
                  </div>
                </div>
              )}

              {selectedGeneration.project_name && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Project</div>
                  <div className="modal-detail-value">{selectedGeneration.project_name}</div>
                </div>
              )}

              <div className="modal-detail-group">
                <div className="modal-detail-label">Created</div>
                <div className="modal-detail-value">
                  {new Date(selectedGeneration.created_at).toLocaleString()}
                </div>
              </div>

              <button
                onClick={() => handleToggleLibrary(selectedGeneration.id, selectedGeneration.in_library)}
                className="btn-modal-library-remove"
              >
                Remove from Prompt Library
                <span className="btn-icon">📚</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptLibrary;
